import React, { useState, useEffect, useMemo } from "react";
import {
  Button,
  Table,
  Form,
  Row,
  Col,
  Card,
  Spinner,
  Alert,
  Modal,
  Badge,
} from "react-bootstrap";
import api from "../../services/api";
import "./Dashboard.css";

const formatDateLocal = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const getDefaultDates = () => {
  const now = new Date();
  const start = new Date(now.getFullYear(), 0, 1);
  return {
    start: formatDateLocal(start),
    end: formatDateLocal(now),
  };
};

const { start: defaultStart, end: defaultEnd } = getDefaultDates();

const formatNumber = (value) =>
  new Intl.NumberFormat("id-ID").format(Number(value) || 0);

const formatRupiah = (value) =>
  new Intl.NumberFormat("id-ID", {
    style: "currency",
    currency: "IDR",
    maximumFractionDigits: 0,
  }).format(Number(value) || 0);

const LaporanReferral = () => {
  const [tglDari, setTglDari] = useState(defaultStart);
  const [tglSampai, setTglSampai] = useState(defaultEnd);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [summary, setSummary] = useState(null);
  const [tarif, setTarif] = useState(null);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [sortConfig, setSortConfig] = useState({
    key: "total_insentif_rupiah",
    direction: "desc",
  });
  const [showDetail, setShowDetail] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);

  const fetchData = async (dari, sampai) => {
    setLoading(true);
    setError("");
    setData([]);
    setSummary(null);
    setTarif(null);
    try {
      const res = await api.get("/pendamping/laporan-referral", {
        params: { tgl_dari: dari, tgl_sampai: sampai },
      });
      if (res.data?.success) {
        setData(res.data.data || []);
        setSummary(res.data.summary || null);
        setTarif(res.data.tarif_insentif || null);
      } else {
        setError(res.data?.message || "Data tidak ditemukan atau response tidak sesuai.");
      }
    } catch (err) {
      setError(
        "Gagal mengambil data: " + (err.response?.data?.message || err.message),
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData(tglDari, tglSampai);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleTampil = (e) => {
    e.preventDefault();
    fetchData(tglDari, tglSampai);
  };

  const filteredData = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return data;
    return data.filter((row) =>
      [
        row.nama,
        row.no_register,
        row.kode_referral,
        row.no_rekening,
        row.nama_rekening,
        row.kode_bank,
      ]
        .filter(Boolean)
        .some((val) => String(val).toLowerCase().includes(q)),
    );
  }, [data, search]);

  const sortedData = useMemo(() => {
    if (!sortConfig.key) return filteredData;
    const sorted = [...filteredData].sort((a, b) => {
      const aVal = a[sortConfig.key] ?? "";
      const bVal = b[sortConfig.key] ?? "";
      if (typeof aVal === "number" && typeof bVal === "number") {
        return sortConfig.direction === "asc" ? aVal - bVal : bVal - aVal;
      }
      return sortConfig.direction === "asc"
        ? String(aVal).localeCompare(String(bVal))
        : String(bVal).localeCompare(String(aVal));
    });
    return sorted;
  }, [filteredData, sortConfig]);

  const handleSort = (key) => {
    setSortConfig((prev) => {
      if (prev.key === key) {
        return { key, direction: prev.direction === "asc" ? "desc" : "asc" };
      }
      return {
        key,
        direction: key === "total_insentif_rupiah" ? "desc" : "asc",
      };
    });
  };

  const sortIcon = (key) => {
    if (sortConfig.key !== key) return "";
    return sortConfig.direction === "asc" ? " ▲" : " ▼";
  };

  const tarifLevel1 = tarif?.["1"] ?? 5000;
  const tarifLevel2 = tarif?.["2"] ?? 3000;
  const tarifLevel3 = tarif?.["3"] ?? 1000;

  const openDetail = (row) => {
    setSelectedRow(row);
    setShowDetail(true);
  };

  const closeDetail = () => {
    setShowDetail(false);
    setSelectedRow(null);
  };

  const renderDetailSection = (level, title, tarifPerSh, items) => {
    const list = items || [];
    const totalSh = list.reduce((sum, item) => sum + (item.jumlah_sh || 0), 0);
    const totalRupiah = list.reduce(
      (sum, item) => sum + (item.insentif_rupiah || 0),
      0,
    );

    return (
      <div className="mb-4">
        <div className="d-flex justify-content-between align-items-center mb-2">
          <h6 className="mb-0">
            {title}{" "}
            <Badge bg="secondary">{formatRupiah(tarifPerSh)}/SH</Badge>
          </h6>
          <span className="small text-muted">
            {formatNumber(totalSh)} SH · {formatRupiah(totalRupiah)}
          </span>
        </div>
        {list.length === 0 ? (
          <div className="text-muted small">Tidak ada downline pada level ini.</div>
        ) : (
          <Table striped bordered hover size="sm" className="mb-0">
            <thead>
              <tr>
                <th>No</th>
                <th>Nama Pendamping</th>
                <th>No Register</th>
                <th className="text-end">Jumlah SH</th>
                <th className="text-end">Insentif</th>
              </tr>
            </thead>
            <tbody>
              {list.map((item, idx) => (
                <tr key={item.id_pendamping || idx}>
                  <td>{idx + 1}</td>
                  <td>{item.nama || "-"}</td>
                  <td>{item.no_register || "-"}</td>
                  <td className="text-end">{formatNumber(item.jumlah_sh)}</td>
                  <td className="text-end">{formatRupiah(item.insentif_rupiah)}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </div>
    );
  };

  return (
    <div>
      <div
        className="page-header-banner"
        style={{
          background:
            "linear-gradient(135deg, var(--primary-color) 0%, var(--primary-hover) 50%, var(--primary-dark) 100%)",
        }}
      >
        <div className="page-header-content">
          <div className="page-header-left">
            <div className="page-header-icon">
              <i className="fas fa-handshake"></i>
            </div>
            <div>
              <h1 className="page-header-title">Laporan Referral</h1>
              <p className="page-header-subtitle">
                Insentif referral 3 level per SH terbit: Level 1 (downline langsung){" "}
                {formatRupiah(tarifLevel1)}, Level 2 {formatRupiah(tarifLevel2)}, Level 3{" "}
                {formatRupiah(tarifLevel3)}. Hanya pendamping DISETUJUI. Filter
                berdasarkan tanggal terbit sertifikat.
              </p>
            </div>
          </div>
        </div>
      </div>

      <Form onSubmit={handleTampil} className="mb-3">
        <Row className="g-3 align-items-end">
          <Col md={3}>
            <Form.Group controlId="tglDari">
              <Form.Label>Periode Terbit — Dari</Form.Label>
              <Form.Control
                type="date"
                value={tglDari}
                onChange={(e) => setTglDari(e.target.value)}
                required
              />
            </Form.Group>
          </Col>
          <Col md={3}>
            <Form.Group controlId="tglSampai">
              <Form.Label>Periode Terbit — Sampai</Form.Label>
              <Form.Control
                type="date"
                value={tglSampai}
                onChange={(e) => setTglSampai(e.target.value)}
                required
              />
            </Form.Group>
          </Col>
          <Col md={4}>
            <Form.Group controlId="search">
              <Form.Label>Cari</Form.Label>
              <Form.Control
                type="text"
                placeholder="Nama, kode referral, no rekening..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </Form.Group>
          </Col>
          <Col md={2}>
            <Button type="submit" variant="primary" disabled={loading} className="w-100">
              {loading ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  Memuat...
                </>
              ) : (
                "Tampil"
              )}
            </Button>
          </Col>
        </Row>
      </Form>

      {error && <Alert variant="danger">{error}</Alert>}

      {!loading && !error && summary && summary.total_sertifikat === 0 && (
        <Alert variant="info">
          Tidak ada sertifikat terbit pada periode{" "}
          <strong>{tglDari}</strong> s/d <strong>{tglSampai}</strong>.
        </Alert>
      )}

      {summary && (
        <Row className="mb-3 g-3">
          <Col md={3}>
            <Card className="h-100 shadow-sm">
              <Card.Body>
                <div className="text-muted small">Pendamping</div>
                <div className="fs-4 fw-bold">{formatNumber(summary.total_pendamping)}</div>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="h-100 shadow-sm">
              <Card.Body>
                <div className="text-muted small">Total SH Terbit</div>
                <div className="fs-4 fw-bold">{formatNumber(summary.total_sertifikat)}</div>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="h-100 shadow-sm border-primary">
              <Card.Body>
                <div className="text-muted small">Total Insentif</div>
                <div className="fs-5 fw-bold text-primary">
                  {formatRupiah(summary.total_insentif_rupiah)}
                </div>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="h-100 shadow-sm">
              <Card.Body>
                <div className="text-muted small">Rincian Insentif</div>
                <div className="small">
                  L1: {formatRupiah(summary.total_insentif_level1_rupiah)}
                </div>
                <div className="small">
                  L2: {formatRupiah(summary.total_insentif_level2_rupiah)}
                </div>
                <div className="small">
                  L3: {formatRupiah(summary.total_insentif_level3_rupiah)}
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      )}

      <div className="table-responsive">
        <Table striped bordered hover size="sm">
          <thead>
            <tr>
              <th>No</th>
              <th style={{ cursor: "pointer" }} onClick={() => handleSort("nama")}>
                Nama{sortIcon("nama")}
              </th>
              <th style={{ cursor: "pointer" }} onClick={() => handleSort("no_register")}>
                No Register{sortIcon("no_register")}
              </th>
              <th style={{ cursor: "pointer" }} onClick={() => handleSort("kode_referral")}>
                Kode Referral{sortIcon("kode_referral")}
              </th>
              <th>No Rekening</th>
              <th style={{ cursor: "pointer" }} onClick={() => handleSort("jumlah_sendiri")}>
                SH Sendiri{sortIcon("jumlah_sendiri")}
              </th>
              <th style={{ cursor: "pointer" }} onClick={() => handleSort("insentif_level1_rupiah")}>
                Insentif L1{sortIcon("insentif_level1_rupiah")}
              </th>
              <th style={{ cursor: "pointer" }} onClick={() => handleSort("insentif_level2_rupiah")}>
                Insentif L2{sortIcon("insentif_level2_rupiah")}
              </th>
              <th style={{ cursor: "pointer" }} onClick={() => handleSort("insentif_level3_rupiah")}>
                Insentif L3{sortIcon("insentif_level3_rupiah")}
              </th>
              <th
                style={{ cursor: "pointer" }}
                onClick={() => handleSort("total_insentif_rupiah")}
              >
                Total Insentif{sortIcon("total_insentif_rupiah")}
              </th>
              <th>Aksi</th>
            </tr>
          </thead>
          <tbody>
            {sortedData.length === 0 ? (
              <tr>
                <td colSpan={11} className="text-center">
                  {loading ? "Memuat..." : "Tidak ada data pada periode ini"}
                </td>
              </tr>
            ) : (
              sortedData.map((row, idx) => (
                <tr key={row.id_pendamping || idx}>
                  <td>{idx + 1}</td>
                  <td>{row.nama || "-"}</td>
                  <td>{row.no_register || "-"}</td>
                  <td>
                    {row.kode_referral ? (
                      <code>{row.kode_referral}</code>
                    ) : (
                      "-"
                    )}
                  </td>
                  <td>
                    <div>{row.no_rekening || "-"}</div>
                    <small className="text-muted">{row.nama_rekening || ""}</small>
                  </td>
                  <td className="text-end">{formatNumber(row.jumlah_sendiri)}</td>
                  <td className="text-end">
                    <div>{formatRupiah(row.insentif_level1_rupiah)}</div>
                    <small className="text-muted">
                      {formatNumber(row.insentif_level1_sh)} SH
                    </small>
                  </td>
                  <td className="text-end">
                    <div>{formatRupiah(row.insentif_level2_rupiah)}</div>
                    <small className="text-muted">
                      {formatNumber(row.insentif_level2_sh)} SH
                    </small>
                  </td>
                  <td className="text-end">
                    <div>{formatRupiah(row.insentif_level3_rupiah)}</div>
                    <small className="text-muted">
                      {formatNumber(row.insentif_level3_sh)} SH
                    </small>
                  </td>
                  <td className="text-end fw-semibold">
                    {formatRupiah(row.total_insentif_rupiah)}
                  </td>
                  <td className="text-center">
                    <Button
                      variant="outline-primary"
                      size="sm"
                      onClick={() => openDetail(row)}
                    >
                      Detil
                    </Button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
          {sortedData.length > 0 && (
            <tfoot>
              <tr className="table-light fw-bold">
                <td colSpan={5} className="text-end">
                  Total Tampil
                </td>
                <td className="text-end">
                  {formatNumber(
                    sortedData.reduce((sum, row) => sum + (row.jumlah_sendiri || 0), 0),
                  )}
                </td>
                <td className="text-end">
                  {formatRupiah(
                    sortedData.reduce(
                      (sum, row) => sum + (row.insentif_level1_rupiah || 0),
                      0,
                    ),
                  )}
                </td>
                <td className="text-end">
                  {formatRupiah(
                    sortedData.reduce(
                      (sum, row) => sum + (row.insentif_level2_rupiah || 0),
                      0,
                    ),
                  )}
                </td>
                <td className="text-end">
                  {formatRupiah(
                    sortedData.reduce(
                      (sum, row) => sum + (row.insentif_level3_rupiah || 0),
                      0,
                    ),
                  )}
                </td>
                <td className="text-end">
                  {formatRupiah(
                    sortedData.reduce(
                      (sum, row) => sum + (row.total_insentif_rupiah || 0),
                      0,
                    ),
                  )}
                </td>
                <td></td>
              </tr>
            </tfoot>
          )}
        </Table>
      </div>

      <Modal show={showDetail} onHide={closeDetail} size="lg" scrollable>
        <Modal.Header closeButton>
          <Modal.Title>Detil Insentif Referral</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedRow && (
            <>
              <div className="mb-3">
                <div className="fw-semibold fs-5">
                  {selectedRow.nama || "Pendamping"}
                </div>
                <div className="text-muted small">
                  {selectedRow.no_register ? `No Register: ${selectedRow.no_register}` : ""}
                  {selectedRow.kode_referral
                    ? ` · Kode Referral: ${selectedRow.kode_referral}`
                    : ""}
                </div>
                <div className="mt-2">
                  SH Sendiri: <strong>{formatNumber(selectedRow.jumlah_sendiri)}</strong>
                  {" · "}
                  Total Insentif:{" "}
                  <strong>{formatRupiah(selectedRow.total_insentif_rupiah)}</strong>
                </div>
              </div>

              {(selectedRow.total_insentif_rupiah || 0) === 0 && (
                <Alert variant="warning" className="small">
                  Insentif Rp 0 biasanya karena belum ada data referral (
                  <code>referral</code> / <code>kode_referral</code>) di database
                  lokal, atau downline belum memiliki SH terbit pada periode ini.
                </Alert>
              )}

              {(selectedRow.total_insentif_rupiah || 0) > 0 &&
                !(
                  (selectedRow.detil_level1?.length || 0) > 0 ||
                  (selectedRow.detil_level2?.length || 0) > 0 ||
                  (selectedRow.detil_level3?.length || 0) > 0
                ) && (
                <Alert variant="info" className="small">
                  Ringkasan insentif ada, tetapi rincian nama downline belum tersedia.
                  Deploy ulang backend terbaru lalu muat ulang laporan.
                </Alert>
              )}

              {renderDetailSection(
                1,
                "Level 1 — Downline Langsung",
                tarifLevel1,
                selectedRow.detil_level1,
              )}
              {renderDetailSection(
                2,
                "Level 2 — Downline Tingkat 2",
                tarifLevel2,
                selectedRow.detil_level2,
              )}
              {renderDetailSection(
                3,
                "Level 3 — Downline Tingkat 3",
                tarifLevel3,
                selectedRow.detil_level3,
              )}
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={closeDetail}>
            Tutup
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default LaporanReferral;
